"use client";

import { Stats, useInfiniteHits } from "react-instantsearch";
import { CreatorCard } from "./CreatorCard";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useSearchStateNavigation } from "./SearchStateManager";
import { useState } from "react";
import type { Doc } from "@/convex/_generated/dataModel";

type CreatorHit = Doc<"creators">;


interface SearchResultsProps {
  campaign?: any;
  campaignId?: string;
  canInvite?: boolean;
  inviteBasePath?: string;
}

// Custom Hits component with infinite scroll/load more functionality
function CustomHits({ campaign, campaignId, canInvite, inviteBasePath }: SearchResultsProps) {
  const { hits, isLastPage, showMore } = useInfiniteHits<CreatorHit>();
  const { navigateToProfile } = useSearchStateNavigation();
  const [isLoading, setIsLoading] = useState(false);

  const handleViewProfile = (creatorId: string) => {
    navigateToProfile(creatorId);
  };

  const handleInviteToCampaign = (creatorId: string, campaignId: string) => {
    // TODO: Implement invite to campaign functionality
    console.log("Invite to campaign:", creatorId, campaignId);
  };

  const handleShowMore = async () => {
    setIsLoading(true);
    try {
      showMore();
    } finally {
      // Reset loading state after a short delay to allow for the new results to load
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  if (hits.length === 0) {
    return (
      <div className="mt-8 text-center">
        <h3 className="text-lg font-semibold">No results found</h3>
        <p className="mt-2 text-muted-foreground">
          Try adjusting your search or filters to find what you're looking for.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
        {hits.map((hit) => (
          <CreatorCard
            key={hit._id}
            creator={hit}
            onViewProfile={handleViewProfile}
            onInviteToCampaign={
              canInvite && campaignId
                ? (creatorId) => handleInviteToCampaign(creatorId, campaignId)
                : undefined
            }
            canInvite={canInvite}
            campaignId={campaignId}
            inviteBasePath={inviteBasePath}
            campaign={campaign ? {
              socialPlatforms: campaign.socialPlatforms,
              name: campaign.name
            } : undefined}
          />
        ))}
      </div>

      {/* Load More Button */}
      {!isLastPage && (
        <div className="flex justify-center mt-8">
          <Button
            onClick={handleShowMore}
            disabled={isLoading}
            variant="outline"
            size="lg"
            className="min-w-[200px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              "Load More Creators"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

// Main SearchResults component
export function SearchResults({
  campaign,
  campaignId,
  canInvite,
  inviteBasePath,
}: SearchResultsProps) {
  return (
    <div>
      <Stats />
      <CustomHits
        campaign={campaign}
        campaignId={campaignId}
        canInvite={canInvite}
        inviteBasePath={inviteBasePath}
      />
    </div>
  );
}
